import type { InvestmentStatus } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import type { EntityManagementActions } from "$root/components/spawnable/entity-management/actions";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { platformToast } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { t } from "i18next";
import { useMemo } from "react";
import { aclByArea } from "../acl/checkers/all";
import { useUserValue } from "../user";
import { universeRepository } from "./repository";
import { universeWithGroupsRepository } from "./universeWithGroupsRepository";

export function useUniverseWithGroupEntityManagementActions(
	universe:
		| {
				name?: string;
				uuid?: string;
				richAcl?: { acl?: RichAccessControl[] };
				referralInvestments?: unknown[];
				status?: InvestmentStatus;
		  }
		| null
		| undefined,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const user = useUserValue();
	return useMemo(
		() => (!universe ? {} : universeEntityManagementActions(user, universe, callbacks, universeWithGroupsRepository)),
		[callbacks, universe, user],
	);
}

export function useUniverseEntityManagementActions(
	universe:
		| {
				name?: string;
				uuid?: string;
				richAcl?: { acl?: RichAccessControl[] };
				referralInvestments?: unknown[];
				status?: InvestmentStatus;
		  }
		| null
		| undefined,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const user = useUserValue();
	return useMemo(
		() => (!universe ? {} : universeEntityManagementActions(user, universe, callbacks, universeRepository)),
		[callbacks, universe, user],
	);
}

export function universeEntityManagementActions(
	user: { id: string },
	universe: {
		name?: string;
		uuid?: string;
		richAcl?: { acl?: RichAccessControl[] };
		referralInvestments?: unknown[];
		status?: InvestmentStatus;
	},
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
	/** @default universeRepository */
	repository: typeof universeRepository | typeof universeWithGroupsRepository = universeRepository,
): Partial<Omit<EntityManagementActions, "createAsync">> {
	return {
		deleteAsync:
			!aclByArea.universe.canDelete(user.id, universe?.richAcl?.acl ?? []) ||
			(universe?.referralInvestments ?? []).length > 0 ||
			universe?.status === "RETRIEVING_DATA"
				? undefined
				: () =>
						spawnDeleteDialog({
							entityName: universe?.name ?? "",
							entityType: "universe",
							yesButton: "Proceed",
							noButton: "Cancel",
							async onDeleteAsync() {
								await runWithErrorReporting(
									async () => {
										try {
											await repository.delete(universe.uuid ?? "");

											platformToast({
												children: t("UNIVERSE.DELETE_OK_MESSAGE", { name: universe.name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onDelete?.(universe.uuid ?? "");
										} catch (err) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
										}
									},
									{
										area: "universe",
										attemptedOperation: {
											message: `delete universe "${universe.uuid}"`,
											payload: JSON.stringify(universe),
										},
									},
								);
							},
						}),
		duplicateAsync:
			!universe.status || invalidStatus.includes(universe.status)
				? undefined
				: () =>
						spawnDuplicateDialog({
							entityType: "universe",
							originalName: universe?.name ?? "",
							checkIfNameIsAvailable: repository.isUniverseNameAvailable,
							onSubmitAsync(name) {
								return runWithErrorReporting(
									async () => {
										try {
											const uuid = (await repository.duplicate(universe.uuid ?? "", name)).uuid ?? "";
											platformToast({
												children: t("UNIVERSE.DUPLICATE_OK_MESSAGE", { name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onDuplicate?.(uuid);

											return uuid;
										} catch (err) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
										}
									},
									{
										area: "universe",
										attemptedOperation: {
											message: `duplicate universe "${universe.uuid}"`,
											payload: JSON.stringify(universe),
										},
									},
								);
							},
						}),
		renameAsync: !aclByArea.universe.canRename(user.id, universe?.richAcl?.acl ?? [])
			? undefined
			: () =>
					spawnRenameDialog({
						entityType: "universe",
						currentName: universe?.name ?? "",
						checkIfNameIsAvailable: repository.isUniverseNameAvailable,
						onSubmitAsync(name) {
							return runWithErrorReporting(
								async () => {
									try {
										await repository.rename(universe.uuid ?? "", name);

										platformToast({
											children: t("UNIVERSE.RENAME_OK_MESSAGE", { name }),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onRename?.(name);

										return name;
									} catch (err) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
									}
								},
								{
									area: "universe",
									attemptedOperation: {
										message: `duplicate universe "${universe.uuid}"`,
										payload: JSON.stringify(universe),
									},
								},
							);
						},
					}),
	};
}

const invalidStatus: Array<InvestmentStatus> = ["REVIEW", "CALCULATING", "RETRIEVING_DATA", "PROPOSAL_READY"];
